import React, { useState, useEffect, useRef } from "react";
import { hrAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, Edit, Trash2, X, Search, Download, Upload, Users, Building2, UserCog, Layers } from "lucide-react";
import { exportCSV, parseCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";

const GENDERS = ["Male", "Female", "Other"];
const SHIFTS = ["Day", "Night", "Rotational"];

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white z-10">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ImportCSVButton({ onImport }) {
  const ref = useRef();
  return (
    <>
      <button onClick={() => ref.current.click()} className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
        <Upload size={14} /> Import CSV
      </button>
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={e => {
        const file = e.target.files[0]; if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => onImport(parseCSV(ev.target.result));
        reader.readAsText(file); e.target.value = '';
      }} />
    </>
  );
}

const TABS = [
  { id: "employees", label: "Employee Master", icon: Users },
  { id: "workers", label: "Workers", icon: UserCog },
  { id: "subcontractors", label: "Sub-Contractors", icon: Building2 },
  { id: "departments", label: "Departments", icon: Layers },
];

export default function HRModule() {
  const { kitchens: ctxKitchens } = useKitchen();
  const { confirm, ConfirmModal } = useConfirm();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterKitchen, setFilterKitchen] = useState("");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    hrAPI.getDepartments().then(r => setDepartments(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchTab(); }, [activeTab, filterDept, filterKitchen, filterType, search]);

  const fetchTab = async () => {
    setLoading(true);
    try {
      if (activeTab === "employees") {
        const params = {};
        if (filterDept) params.department = filterDept;
        if (filterKitchen) params.work_location = filterKitchen;
        if (search) params.search = search;
        const r = await hrAPI.getEmployees(params);
        setEmployees(r.data || []);
      } else if (activeTab === "workers") {
        const params = {};
        if (filterDept) params.department = filterDept;
        if (filterKitchen) params.kitchen_id = filterKitchen;
        if (filterType) params.worker_type = filterType;
        if (search) params.search = search;
        const r = await hrAPI.getWorkers(params);
        setWorkers(r.data || []);
      } else if (activeTab === "subcontractors") {
        const params = {};
        if (filterKitchen) params.kitchen_id = filterKitchen;
        const r = await hrAPI.getSubcontractors(params);
        setSubcontractors(r.data || []);
      } else if (activeTab === "departments") {
        const r = await hrAPI.getDepartments();
        setDepartments(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const openCreate = () => {
    setForm({});
    setModal({ type: activeTab, mode: "create" });
    setError("");
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setModal({ type: activeTab, mode: "edit", id: item.id });
    setError("");
  };

  const handleSave = async () => {
    setError("");
    try {
      if (activeTab === "employees") {
        if (modal.mode === "create") await hrAPI.createEmployee(form);
        else await hrAPI.updateEmployee(modal.id, form);
      } else if (activeTab === "workers") {
        if (modal.mode === "create") await hrAPI.createWorker(form);
        else await hrAPI.updateWorker(modal.id, form);
      } else if (activeTab === "subcontractors") {
        if (modal.mode === "create") await hrAPI.createSubcontractor(form);
        else await hrAPI.updateSubcontractor(modal.id, form);
      } else if (activeTab === "departments") {
        if (modal.mode === "create") await hrAPI.createDepartment({ name: form.name });
        else await hrAPI.updateDepartment(modal.id, { name: form.name });
      }
      setModal(null);
      setSuccess("Saved!"); setTimeout(() => setSuccess(""), 2000);
      fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Are you sure?")) return;
    try {
      if (activeTab === "employees") await hrAPI.deleteEmployee(id);
      else if (activeTab === "workers") await hrAPI.deleteWorker(id);
      else if (activeTab === "subcontractors") await hrAPI.deleteSubcontractor(id);
      else if (activeTab === "departments") await hrAPI.deleteDepartment(id);
      fetchTab();
    } catch (e) { alert(formatError(e)); }
  };

  const handleImportEmployees = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      try {
        await hrAPI.createEmployee({
          employee_code: row.employee_code || row["Employee Code"] || "",
          name: row.name || row["Name"] || "",
          gender: row.gender || row["Gender"] || "",
          dob: row.dob || row["DOB"] || null,
          doj: row.doj || row["DOJ"] || null,
          department: row.department || row["Department"] || "",
          designation: row.designation || row["Designation"] || "",
          work_location: row.work_location || row["Work Location"] || "",
          phone: row.phone || row["Phone"] || "",
          email: row.email || row["Email"] || "",
        });
        ok++;
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }
    setSuccess(`Imported ${ok} employees`); setTimeout(() => setSuccess(""), 3000);
    fetchTab();
  };

  const handleImportWorkers = async (rows) => {
    let ok = 0;
    for (const row of rows) {
      try {
        await hrAPI.createWorker({
          worker_id: row.worker_id || row["Worker ID"] || "",
          name: row.name || row["Name"] || "",
          worker_type: row.worker_type || row["Type"] || "temporary",
          department: row.department || row["Department"] || "",
          shift: row.shift || "Day",
          phone: row.phone || "",
        });
        ok++;
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }
    setSuccess(`Imported ${ok} workers`); setTimeout(() => setSuccess(""), 3000);
    fetchTab();
  };

  const kName = (id) => kitchens.find(k => k.id === id)?.name || id || "—";
  const subName = (id) => subcontractors.find(s => s.id === id)?.name || "—";

  return (
    <div className="space-y-6 max-w-6xl" data-testid="hr-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">HR Management</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Employees, workers, sub-contractors & departments</p>
        </div>
        <button onClick={openCreate}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
          <Plus size={14} /> Add {TABS.find(t => t.id === activeTab)?.label.split(" ")[0]}
        </button>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setSearch(""); setFilterDept(""); setFilterKitchen(""); setFilterType(""); }}
            className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeTab !== "departments" && (
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
            <input placeholder={`Search ${activeTab}...`} value={search} onChange={e => setSearch(e.target.value)}
              className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
          </div>
          {(activeTab === "employees" || activeTab === "workers") && (
            <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
              value={filterDept} onChange={e => setFilterDept(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          )}
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={filterKitchen} onChange={e => setFilterKitchen(e.target.value)}>
            <option value="">All Kitchens</option>
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          {activeTab === "workers" && (
            <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
              value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="subcontractor">Sub-contractor</option>
              <option value="temporary">Temporary (Manna)</option>
            </select>
          )}
          <div className="flex gap-2 ml-auto">
            {activeTab === "employees" && (
              <>
                <ImportCSVButton onImport={handleImportEmployees} />
                <button onClick={() => exportCSV(employees.map(e => ({ employee_code: e.employee_code, name: e.name, gender: e.gender, dob: e.dob, doj: e.doj, department: e.department, designation: e.designation, work_location: e.work_location, phone: e.phone, email: e.email })), "employees")}
                  className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export
                </button>
              </>
            )}
            {activeTab === "workers" && (
              <>
                <ImportCSVButton onImport={handleImportWorkers} />
                <button onClick={() => exportCSV(workers.map(w => ({ worker_id: w.worker_id, name: w.name, type: w.worker_type, subcontractor: w.subcontractor_name, department: w.department, shift: w.shift, phone: w.phone })), "workers")}
                  className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* EMPLOYEES */}
          {activeTab === "employees" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Code", "Name", "Gender", "DOB", "DOJ", "Department", "Designation", "Kitchen", "Phone", ""].map(h => (
                      <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {employees.map(e => (
                      <tr key={e.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-3 py-3 font-bold text-[#E8956D]">{e.employee_code}</td>
                        <td className="px-3 py-3 font-medium text-[#3A322C]">{e.name}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{e.gender || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73] whitespace-nowrap">{e.dob || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73] whitespace-nowrap">{e.doj || "—"}</td>
                        <td className="px-3 py-3"><span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{e.department || "—"}</span></td>
                        <td className="px-3 py-3 text-[#8A7E73]">{e.designation || "—"}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{kName(e.work_location)}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{e.phone || "—"}</td>
                        <td className="px-3 py-3"><div className="flex gap-2">
                          <button onClick={() => openEdit(e)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(e.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                        </div></td>
                      </tr>
                    ))}
                    {employees.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-[#8A7E73]">No employees found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* WORKERS */}
          {activeTab === "workers" && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Worker ID", "Name", "Type", "Sub-Contractor", "Department", "Shift", "Phone", ""].map(h => (
                      <th key={h} className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {workers.map(w => (
                      <tr key={w.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-3 py-3 font-bold text-[#E8956D]">{w.worker_id}</td>
                        <td className="px-3 py-3 font-medium text-[#3A322C]">{w.name}</td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${w.worker_type === "subcontractor" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                            {w.worker_type === "subcontractor" ? "Sub-contractor" : "Temporary"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-[#8A7E73]">{w.subcontractor_name || "—"}</td>
                        <td className="px-3 py-3"><span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{w.department || "—"}</span></td>
                        <td className="px-3 py-3 text-[#8A7E73]">{w.shift || "Day"}</td>
                        <td className="px-3 py-3 text-[#8A7E73]">{w.phone || "—"}</td>
                        <td className="px-3 py-3"><div className="flex gap-2">
                          <button onClick={() => openEdit(w)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(w.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                        </div></td>
                      </tr>
                    ))}
                    {workers.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8A7E73]">No workers found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SUBCONTRACTORS */}
          {activeTab === "subcontractors" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subcontractors.map(s => (
                <div key={s.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-[#3A322C]">{s.name}</h3>
                      <p className="text-sm text-[#8A7E73] mt-0.5">{s.contact_person || "—"}</p>
                      <p className="text-xs text-[#8A7E73]">{s.phone || ""} {s.email ? `· ${s.email}` : ""}</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => openEdit(s)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(s.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full font-bold">{s.worker_count || 0} workers</span>
                    {s.kitchen_id && <span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{kName(s.kitchen_id)}</span>}
                  </div>
                </div>
              ))}
              {subcontractors.length === 0 && <div className="col-span-3 text-center py-16 text-[#8A7E73]">No sub-contractors added</div>}
            </div>
          )}

          {/* DEPARTMENTS */}
          {activeTab === "departments" && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {departments.map(d => (
                <div key={d.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex items-center justify-between">
                  <span className="font-medium text-[#3A322C] text-sm">{d.name}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => openEdit(d)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={13} /></button>
                    <button onClick={() => handleDelete(d.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* MODALS */}
      {modal && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} ${TABS.find(t => t.id === modal.type)?.label}`} onClose={() => setModal(null)} wide={modal.type === "employees"}>
          <div className="space-y-4">
            {/* EMPLOYEE FORM */}
            {modal.type === "employees" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Employee Code *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.employee_code || ""} onChange={e => setForm({ ...form, employee_code: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Full Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Gender</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.gender || ""} onChange={e => setForm({ ...form, gender: e.target.value })}>
                      <option value="">Select</option>{GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date of Birth</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.dob || ""} onChange={e => setForm({ ...form, dob: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date of Joining</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.doj || ""} onChange={e => setForm({ ...form, doj: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Department</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.department || ""} onChange={e => setForm({ ...form, department: e.target.value })}>
                      <option value="">Select</option>{departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Designation</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.designation || ""} onChange={e => setForm({ ...form, designation: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Work Location (Kitchen)</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.work_location || ""} onChange={e => setForm({ ...form, work_location: e.target.value })}>
                      <option value="">Select Kitchen</option>{kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reports To (Employee Code)</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="Manager's employee code" value={form.reports_to || ""} onChange={e => setForm({ ...form, reports_to: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Email</label><input type="email" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* WORKER FORM */}
            {modal.type === "workers" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Worker ID *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.worker_id || ""} onChange={e => setForm({ ...form, worker_id: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Full Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Worker Type *</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.worker_type || ""} onChange={e => setForm({ ...form, worker_type: e.target.value })}>
                      <option value="">Select</option><option value="subcontractor">Sub-contractor</option><option value="temporary">Temporary (Manna)</option>
                    </select></div>
                  {form.worker_type === "subcontractor" && (
                    <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Sub-Contractor</label>
                      <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.subcontractor_id || ""} onChange={e => setForm({ ...form, subcontractor_id: e.target.value })}>
                        <option value="">Select</option>{subcontractors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select></div>
                  )}
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Department</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.department || ""} onChange={e => setForm({ ...form, department: e.target.value })}>
                      <option value="">Select</option>{departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Shift</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.shift || "Day"} onChange={e => setForm({ ...form, shift: e.target.value })}>
                      {SHIFTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
                    <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value })}>
                      <option value="">Select</option>{kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                    </select></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
              </>
            )}

            {/* SUBCONTRACTOR FORM */}
            {modal.type === "subcontractors" && (
              <div className="space-y-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Company Name *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Contact Person</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.contact_person || ""} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Email</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Address</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value || null })}>
                    <option value="">All Kitchens</option>{kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select></div>
              </div>
            )}

            {/* DEPARTMENT FORM */}
            {modal.type === "departments" && (
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Department Name *</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            )}

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}
    </div>
  );
}
